import { useCallback, useEffect, useState, useRef } from 'react';
import { listDistributionGroups } from '../../services/distribution/distribution.service';
import { listAttachmentGroups } from '../../services/attachments/attachment.service';
import type { DistributionGroupSummary } from '../../services/distribution/distribution.types';
import type { AttachmentGroupDto } from '../../services/attachments/attachment.types';
import {
    getUniqueAlarmPaths,
    getSite,
    listVirtualTenants,
    getVirtualTenantAlarmPaths,
    type UniqueAlarmPathItem,
    type VirtualTenantAlarmPathItem
} from '../../services/siteManagement/siteManagement.service';
import { useSiteTree } from '../../hooks/useSiteTree';
import { useFormValidation } from '../../hooks/useFormValidation';
import { validators } from '../../helpers/validators';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { StatusMessage } from '../ui/StatusMessage';
import NumericChipsInput from '../inputs/NumericChipsInput';

const SEARCH_SELECTOR = '.select2-dropdown__search';

type GenericListItemProps = {
    id: string;
    label: string;
    ruleIdx: number;
    selected: boolean;
    onSelect: (ruleIdx: number, id: string) => void;
    onClearSearch: (ruleIdx: number) => void;
    onClose: () => void;
};

const GenericListItem: React.FC<GenericListItemProps> = ({ id, label, ruleIdx, selected, onSelect, onClearSearch, onClose }) => {
    const handleSelect = (e?: React.MouseEvent | React.KeyboardEvent) => {
        if (e && 'preventDefault' in e) {e.preventDefault();}
        onSelect(ruleIdx, id);
        onClearSearch(ruleIdx);
        // Delay close slightly to ensure parent onChange has propagated
        setTimeout(() => onClose(), 0);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect(e);
        }
    };
    return (
        <li className="select2-list-item">
            <button
                type="button"
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                aria-pressed={selected}
            >
                <span className="select2-list-item__label">{label}</span>
                <span className="select2-list-item__badge">{selected ? <span className="se-icon--acknowledged" aria-hidden="true"></span> : ''}</span>
            </button>
        </li>
    );
};

export interface RuleFormData {
    ruleName?: string;
    isActive?: boolean;
    isUltraCritical: boolean;
    bindPathPriority?: boolean;
    distributionGroupIds?: string;
    attachmentGroupIds?: string;
    /** Comma-separated site ids that this rule applies to (building selection) */
    appliesToSiteIds?: string;
    /** Comma-separated VT ids that this rule applies to */
    appliesToVirtualTenantIds?: string;
    filteringPaths?: string;
    filteringPathLabelsById?: Record<string, string>;
    filteringPriorities?: number[];
    hideRtn?: boolean;
    rtnExceptions?: string;
}

type VirtualTenantOption = {
    id: string;
    title: string;
    parentSiteId?: number;
    associatedAlarmPaths?: number[];
};

type SiteOption = {
    id: string;
    title: string;
    customerId?: number | null;
};

type AlarmPathRow = UniqueAlarmPathItem & Partial<VirtualTenantAlarmPathItem>;

interface RulesFormProps {
    value: RuleFormData[];
    onChange: (d: RuleFormData[]) => void;
    disabled?: boolean;
    siteId?: number;
    associatedAlarmPaths?: Array<{ id?: number; alarmPathId?: number; pathToAlarm?: string }>;
    onCancelEditRule?: () => void;
    /** Optional edit actions to render inside a specific rule-entry (e.g. Cancel/Update buttons) */
    editActions?: React.ReactNode;
    /** The rule index at which to render editActions (default: 0) */
    editIndex?: number;
    /** Hide the "Applies to Building" selector (used when creating rules for a specific site) */
    hideBuildingSelector?: boolean;
    /** Hide the "Add rule" button (used when editing a single rule) */
    hideAddRuleButton?: boolean;
    /** Hide the "Apply to Virtual Tenant(s)" selector */
    hideVirtualTenantSelector?: boolean;
    /** Validation callback */
    onValidate?: (isValid: boolean, errors: any[]) => void;
    /** Show error summary */
    showErrorSummary?: boolean;
    /** Close error summary callback */
    onCloseErrorSummary?: () => void;
}

const RulesForm: React.FC<RulesFormProps> = ({ value, onChange, disabled = false, siteId, associatedAlarmPaths, editActions, editIndex, onCancelEditRule, hideBuildingSelector = false, hideAddRuleButton = false, hideVirtualTenantSelector = false, onValidate, showErrorSummary = false, onCloseErrorSummary }) => {
    const [groups, setGroups] = useState<DistributionGroupSummary[]>([]);
    const [attachmentGroups, setAttachmentGroups] = useState<AttachmentGroupDto[]>([]);
    const [pathsMap, setPathsMap] = useState<Record<number, UniqueAlarmPathItem[]>>({});
    const [customers, setCustomers] = useState<Array<{ id: number; customerName: string }>>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const pathSearchRef = useRef<HTMLInputElement | null>(null);
    const [pathSearchMap, setPathSearchMap] = useState<Record<number, string>>({});
    const [openPathIndex, setOpenPathIndex] = useState<number | null>(null);
    const pathWrapperRef = useRef<HTMLDivElement | null>(null);
    const distWrapperRef = useRef<HTMLDivElement | null>(null);
    const attWrapperRef = useRef<HTMLDivElement | null>(null);
    const [openDistIndex, setOpenDistIndex] = useState<number | null>(null);
    const [openAttIndex, setOpenAttIndex] = useState<number | null>(null);
    const [distSearchMap, setDistSearchMap] = useState<Record<number, string>>({});
    const [attSearchMap, setAttSearchMap] = useState<Record<number, string>>({});
    const errorSummaryRef = useRef<HTMLDivElement | null>(null);

    // Sites (buildings) select helpers
    const { nodes: siteNodes, loading: sitesLoading } = useSiteTree();
    const [siteSearchMap, setSiteSearchMap] = useState<Record<number, string>>({});
    const [openSiteIndex, setOpenSiteIndex] = useState<number | null>(null);
    const siteWrapperRef = useRef<HTMLDivElement | null>(null);
    const [availableSitesForCustomer, setAvailableSitesForCustomer] = useState<SiteOption[]>([]);
    const [currentSiteCustomerId, setCurrentSiteCustomerId] = useState<number | null>(null);
    const [availableVirtualTenantsMap, setAvailableVirtualTenantsMap] = useState<Record<number, VirtualTenantOption[]>>({});
    const [vtSearchMap, setVtSearchMap] = useState<Record<number, string>>({});
    const [openVtIndex, setOpenVtIndex] = useState<number | null>(null);
    const vtWrapperRef = useRef<HTMLDivElement | null>(null);

    const parseCsvIds = useCallback((csv?: string): string[] => {
        return (csv || '').split(',').map(s => s.trim()).filter(Boolean);
    }, []);

    const mergeUniquePaths = useCallback((pathBatches: UniqueAlarmPathItem[][]): UniqueAlarmPathItem[] => {
        const merged: UniqueAlarmPathItem[] = [];
        const seen = new Set<string>();
        pathBatches.forEach(batch => {
            batch.forEach(item => {
                const key = String(item.id);
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(item);
                }
            });
        });
        return merged;
    }, []);

    const loadVirtualTenantsForSites = useCallback(async (ruleIndex: number, selectedSiteIds: string[]) => {
        if (!selectedSiteIds.length) {
            setAvailableVirtualTenantsMap(prev => ({ ...prev, [ruleIndex]: [] }));
            return;
        }

        try {
            const vtRes = await listVirtualTenants();
            const allVirtualTenants = vtRes?.success && Array.isArray(vtRes.data) ? vtRes.data : [];
            const nextOptions: VirtualTenantOption[] = [];
            const seen = new Set<string>();

            for (const selectedSiteId of selectedSiteIds) {
                const parentSiteId = Number(selectedSiteId);
                const numericSelectedSiteId = Number(selectedSiteId);
                const vtsForSite = allVirtualTenants.filter(vt => {
                    const candidateIds = [vt.siteIdentifierRegistryId, vt.parentId, vt.parentSiteHierarchyId]
                        .map(id => Number(id))
                        .filter(id => !Number.isNaN(id) && id > 0);
                    return candidateIds.includes(numericSelectedSiteId) || candidateIds.includes(parentSiteId);
                });

                vtsForSite.forEach(vt => {
                    const id = String(vt.id);
                    if (!id || seen.has(id)) {
                        return;
                    }
                    seen.add(id);
                    const baseTitle = vt.name || vt.virtualTenantIdentifier || `VT ${id}`;
                    nextOptions.push({
                        id,
                        title: selectedSiteIds.length > 1 ? `${baseTitle} (Site ${parentSiteId})` : baseTitle,
                        parentSiteId,
                        associatedAlarmPaths: Array.isArray(vt.associatedAlarmPaths)
                            ? vt.associatedAlarmPaths.map(pathId => Number(pathId)).filter(pathId => !Number.isNaN(pathId) && pathId > 0)
                            : [],
                    });
                });
            }

            setAvailableVirtualTenantsMap(prev => ({ ...prev, [ruleIndex]: nextOptions }));
        } catch {
            setAvailableVirtualTenantsMap(prev => ({ ...prev, [ruleIndex]: [] }));
        }
    }, []);

    const loadPathsForRule = useCallback(async (ruleIndex: number, selectedSiteIds: string[], selectedVirtualTenantIds: string[]) => {
        try {
            if (associatedAlarmPaths !== undefined) {
                return;
            }

            // VT rules from parent site paths
            if (selectedVirtualTenantIds.length > 0 && selectedSiteIds.length === 0) {
                const vtPathResponses = await Promise.all(selectedVirtualTenantIds.map(vtId => getVirtualTenantAlarmPaths(vtId)));
                const batches = vtPathResponses
                    .map(res => {
                        if (!res?.success || !Array.isArray(res.data)) {
                            return [] as UniqueAlarmPathItem[];
                        }

                        return res.data
                            .map(item => {
                                const pathId = Number(item.alarmPathId ?? item.id);
                                if (Number.isNaN(pathId) || pathId <= 0) {
                                    return null;
                                }

                                return {
                                    id: pathId,
                                    pathToAlarm: item.pathToAlarm || String(pathId),
                                } as UniqueAlarmPathItem;
                            })
                            .filter((item): item is UniqueAlarmPathItem => item !== null);
                    });
                setPathsMap(prev => ({ ...prev, [ruleIndex]: mergeUniquePaths(batches) }));
                return;
            }

            if (selectedSiteIds.length > 0) {
                const sitePathResponses = await Promise.all(selectedSiteIds.map(id => getUniqueAlarmPaths(Number(id))));
                const batches = sitePathResponses
                    .map(res => (res?.success && Array.isArray(res.data) ? res.data : []))
                    .filter(Boolean) as UniqueAlarmPathItem[][];
                setPathsMap(prev => ({ ...prev, [ruleIndex]: mergeUniquePaths(batches) }));
                return;
            }

            setPathsMap(prev => ({ ...prev, [ruleIndex]: [] }));
        } catch {
            setPathsMap(prev => ({ ...prev, [ruleIndex]: [] }));
        }
    }, [mergeUniquePaths]);

    const handleSiteSelectionChange = useCallback(async (ruleIndex: number, selectedSiteIds: string[]) => {
        const next = (value || []).map((r, idx) => {
            if (idx !== ruleIndex) {
                return r;
            }
            return {
                ...r,
                appliesToSiteIds: selectedSiteIds.join(','),
                appliesToVirtualTenantIds: '',
            };
        });
        onChange(next);
        await loadVirtualTenantsForSites(ruleIndex, selectedSiteIds);
        await loadPathsForRule(ruleIndex, selectedSiteIds, []);
    }, [loadPathsForRule, loadVirtualTenantsForSites, onChange, value]);

    const handleVirtualTenantSelectionChange = useCallback(async (ruleIndex: number, selectedVirtualTenantId: string | null) => {
        const nextSelectedVirtualTenantIds = selectedVirtualTenantId ? [selectedVirtualTenantId] : [];
        updateRule(ruleIndex, 'appliesToVirtualTenantIds', nextSelectedVirtualTenantIds.join(','));
        const selectedSiteIds = parseCsvIds(value[ruleIndex]?.appliesToSiteIds);
        await loadPathsForRule(ruleIndex, selectedSiteIds, nextSelectedVirtualTenantIds);
    }, [loadPathsForRule, parseCsvIds, value]);

    const mapSiteNodeToOption = useCallback((n: any): SiteOption | null => {
        let numericId: number | null = null;
        if (typeof n?.sirId === 'number' && n.sirId > 0) {
            numericId = n.sirId;
        } else if (typeof n?.id === 'number' && n.id > 0) {
            numericId = n.id;
        }

        if (!numericId) {
            return null;
        }

        return {
            id: String(numericId),
            title: n?.title || String(numericId),
            customerId: n?.customerId,
        };
    }, [associatedAlarmPaths, mergeUniquePaths]);

    const findCustomerIdBySiteIdInTree = useCallback((nodes: any[], lookupSiteId: number): number | null => {
        const directSiteMatch = nodes
            .filter((n: any) => n?.type === 'customer')
            .flatMap((n: any) => n?.children || [])
            .find((n: any) => n?.type === 'site' && (Number(n?.sirId) === lookupSiteId || Number(n?.shId) === lookupSiteId || Number(n?.id) === lookupSiteId));

        if (directSiteMatch && typeof directSiteMatch.customerId === 'number') {
            return directSiteMatch.customerId;
        }

        return null;
    }, []);

    const collectAllSitesFromTree = useCallback((nodes: any[]): SiteOption[] => {
        const collected = nodes
            .filter((n: any) => n?.type === 'customer')
            .flatMap((n: any) => (n?.children || []).filter((c: any) => c?.type === 'site'))
            .map(mapSiteNodeToOption)
            .filter((s: SiteOption | null): s is SiteOption => Boolean(s));

        const seen = new Set<string>();
        return collected.filter(s => {
            if (seen.has(s.id)) {
                return false;
            }
            seen.add(s.id);
            return true;
        });
    }, [mapSiteNodeToOption]);

    useEffect(() => {
        let mounted = true;
        listDistributionGroups()
            .then(res => {
                if (!mounted) {
                    return;
                }
                if (res?.success && res.data) {
                    setGroups(res.data);
                }
            })
            .catch(() => {});

        listAttachmentGroups()
            .then(res => {
                if (!mounted) {
                    return;
                }
                if (res?.success && res.data) {
                    setAttachmentGroups(res.data);
                }
            })
            .catch(() => {});

        // Fetch customers for the customer selector
        import('../../services/customer/customer.service').then(({ listCustomers }) => {
            listCustomers()
                .then(res => {
                    if (!mounted) {return;}
                    if (res?.success && res.data) {
                        setCustomers(res.data);
                    }
                })
                .catch(() => {});
        });

        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        // For each rule, if it has a site, fetch alarm paths if not already loaded
        (value || []).forEach((rule, idx) => {
            const selectedSiteIds = siteId
                ? [String(siteId)]
                : parseCsvIds(rule.appliesToSiteIds);
            const selectedVirtualTenantIds = parseCsvIds(rule.appliesToVirtualTenantIds);

            if (selectedSiteIds.length > 0 && !(idx in availableVirtualTenantsMap)) {
                void loadVirtualTenantsForSites(idx, selectedSiteIds);
            }

            if ((selectedSiteIds.length > 0 || selectedVirtualTenantIds.length > 0) && !(idx in pathsMap)) {
                void loadPathsForRule(idx, selectedSiteIds, selectedVirtualTenantIds);
            }
        });
    }, [value, siteId, parseCsvIds, pathsMap, availableVirtualTenantsMap, loadVirtualTenantsForSites, loadPathsForRule]);

    // Resolve current site customer for site-scoped forms.
    // Try site tree first (supports both registry and hierarchy ids), then fallback to API.
    useEffect(() => {
        if (!siteId) {
            setCurrentSiteCustomerId(null);
            return;
        }

        if (!Array.isArray(siteNodes) || siteNodes.length === 0) {
            // Wait for tree data; this avoids noisy /sites/{id} fallback calls before tree is available.
            return;
        }

        const numericSiteId = Number(siteId);
        if (!Number.isNaN(numericSiteId)) {
            const fromTree = findCustomerIdBySiteIdInTree(siteNodes as any[], numericSiteId);
            if (fromTree) {
                setCurrentSiteCustomerId(fromTree);
                if (!selectedCustomerId) {
                    setSelectedCustomerId(fromTree);
                }
                return;
            }
        }

        let mounted = true;
        (async () => {
            try {
                const res = await getSite(siteId);
                if (!mounted) {return;}
                const cid = res?.success && res.data?.customerId ? res.data.customerId : null;
                setCurrentSiteCustomerId(cid ?? null);
                if (cid && !selectedCustomerId) {
                    setSelectedCustomerId(cid);
                }
            } catch (err) {
                console.info('[RulesForm] getSite error', err);
                if (mounted) {
                    // Keep form usable even when /sites/{id} cannot resolve (e.g. hierarchy id)
                    setCurrentSiteCustomerId(null);
                }
            }
        })();
        return () => { mounted = false; };
    }, [siteId, siteNodes, findCustomerIdBySiteIdInTree, selectedCustomerId]);

    // Build available site options from tree. If customer cannot be resolved for site-scoped edit,
    // fallback to all sites so rules can still be applied to sites.
    useEffect(() => {
        const customerIdToUse = currentSiteCustomerId || selectedCustomerId;
        if (!siteNodes) {
            setAvailableSitesForCustomer([]);
            return;
        }

        if (customerIdToUse) {
            const customerNode = siteNodes.find(n => n.type === 'customer' && n.customerId === customerIdToUse);
            if (!customerNode || !customerNode.children) {
                console.info('[RulesForm] No customer node found for customerId:', customerIdToUse);
                setAvailableSitesForCustomer([]);
                return;
            }

            const sites = customerNode.children
                .filter(n => n.type === 'site')
                .map(n => mapSiteNodeToOption(n as any))
                .filter((s: SiteOption | null): s is SiteOption => Boolean(s));

            setAvailableSitesForCustomer(sites);
            return;
        }

        if (siteId) {
            setAvailableSitesForCustomer(collectAllSitesFromTree(siteNodes as any[]));
            return;
        }

        setAvailableSitesForCustomer([]);
    }, [currentSiteCustomerId, selectedCustomerId, siteNodes, siteId, mapSiteNodeToOption, collectAllSitesFromTree]);

    // close dropdowns on outside click
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (openDistIndex !== null) {
                if (!distWrapperRef.current?.contains(t)) {setOpenDistIndex(null);}
            }
            if (openAttIndex !== null) {
                if (!attWrapperRef.current?.contains(t)) {setOpenAttIndex(null);}
            }
            if (openVtIndex !== null) {
                if (!vtWrapperRef.current?.contains(t)) {setOpenVtIndex(null);}
            }
            // also clear path searches / close path dropdown if click outside
            if (openPathIndex !== null) {
                if (!pathWrapperRef.current?.contains(t)) {
                    setOpenPathIndex(null);
                    setPathSearchMap({});
                }
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [openDistIndex, openAttIndex, openVtIndex, openPathIndex]);

    // Helpers for distribution/attachment selection to avoid deep nested inline handlers
    const toggleDistForRule = (ruleIndex: number, id: string) => {
        const cur = (value[ruleIndex]?.distributionGroupIds || '').split(',').filter(Boolean);
        const next = cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id];
        updateRule(ruleIndex, 'distributionGroupIds', next.join(','));
    };

    const removeDistFromRule = (ruleIndex: number, id: string) => {
        const cur = (value[ruleIndex]?.distributionGroupIds || '').split(',').filter(Boolean);
        const next = cur.filter(s => s !== id);
        updateRule(ruleIndex, 'distributionGroupIds', next.join(','));
    };

    const toggleAttForRule = (ruleIndex: number, id: string) => {
        const cur = (value[ruleIndex]?.attachmentGroupIds || '').split(',').filter(Boolean);
        const next = cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id];
        updateRule(ruleIndex, 'attachmentGroupIds', next.join(','));
    };

    const removeAttFromRule = (ruleIndex: number, id: string) => {
        const cur = (value[ruleIndex]?.attachmentGroupIds || '').split(',').filter(Boolean);
        const next = cur.filter(s => s !== id);
        updateRule(ruleIndex, 'attachmentGroupIds', next.join(','));
    };

    const clearDistSearch = (ri: number) => setDistSearchMap(prev => ({ ...prev, [ri]: '' }));
    const clearAttSearch = (ri: number) => setAttSearchMap(prev => ({ ...prev, [ri]: '' }));
    const clearVtSearch = (ri: number) => setVtSearchMap(prev => ({ ...prev, [ri]: '' }));

    const addRule = () => {
        const next: RuleFormData = {
            ruleName: '',
            isActive: true,
            bindPathPriority: false,
            distributionGroupIds: '',
            attachmentGroupIds: '',
            appliesToVirtualTenantIds: '',
            filteringPaths: '',
            filteringPriorities: [],
            hideRtn: false,
            rtnExceptions: '',
            isUltraCritical: false
        };
        onChange([...(value || []), next]);
    };

    // path helpers to avoid inline lambdas
    const clearPathSearchForRule = (ri: number) => setPathSearchMap(prev => ({ ...prev, [ri]: '' }));
    const closePath = () => setOpenPathIndex(null);
    const closeDist = () => setOpenDistIndex(null);
    const closeAtt = () => setOpenAttIndex(null);
    const closeVt = () => setOpenVtIndex(null);

    const updateRule = (i: number, k: keyof RuleFormData, v: any) => {
        const next = (value || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r);
        onChange(next);
    };

    const removeRule = (i: number) => onChange((value || []).filter((_, idx) => idx !== i));

    // Validation
    const { errors, validateAll } = useFormValidation({
        initialValues: value.map(rule => ({
            ruleName: rule.ruleName || '',
            filteringPriorities: rule.filteringPriorities || [],
            filteringPaths: rule.filteringPaths || '',
            appliesToSiteIds: rule.appliesToSiteIds || ''
        })),
        rules: {
            ruleName: [validators.required('Rule Name is required')],
            appliesToSiteIds: [((val: unknown) => {
                // Only validate if building selector is visible (not hidden and no siteId prop)
                const strVal = typeof val === 'string' ? val : '';
                if (!hideBuildingSelector && !siteId) {
                    if (!strVal || strVal.trim() === '') {
                        return 'At least one building must be selected';
                    }
                }
                return null;
            })],
            filteringPriorities: [((val: unknown, allValues: any) => {
                const arrVal = Array.isArray(val) ? val as number[] : [];
                const hasNoPaths = !allValues.filteringPaths || allValues.filteringPaths.trim() === '';
                const hasNoPriorities = !arrVal || arrVal.length === 0;
                if (hasNoPaths && hasNoPriorities) {
                    return 'Either Path IDs or Priorities must be selected';
                }
                return null;
            })],
            filteringPaths: [((val: unknown, allValues: any) => {
                const strVal = typeof val === 'string' ? val : '';
                const hasNoPaths = !strVal || strVal.trim() === '';
                const hasNoPriorities = !allValues.filteringPriorities || allValues.filteringPriorities.length === 0;
                if (hasNoPaths && hasNoPriorities) {
                    return 'Either Path IDs or Priorities must be selected';
                }
                return null;
            })]
        }
    });

    // Expose validation to parent
    useEffect(() => {
        if (onValidate) {
            const formData = value.map(rule => ({
                ruleName: rule.ruleName || '',
                filteringPriorities: rule.filteringPriorities || [],
                filteringPaths: rule.filteringPaths || '',
                appliesToSiteIds: rule.appliesToSiteIds || ''
            }));
            const validationErrors = validateAll(formData as any);
            const hasErrors = Array.isArray(validationErrors)
                ? validationErrors.some(v => v && Object.keys(v).length > 0)
                : validationErrors && Object.keys(validationErrors).length > 0;
            onValidate(!hasErrors, validationErrors || []);
        }
    }, [value, onValidate]);

    // Scroll to error summary when validation errors appear
    useEffect(() => {
        if (showErrorSummary && errors.some(err => err && Object.keys(err).length > 0)) {
            requestAnimationFrame(() => {
                errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }, [showErrorSummary, errors]);

    // per-rule path search map is used inside the rule render loop
    return (
        <div className="form__section mt-1">
            {showErrorSummary && [
                ...new Set(
                    errors.flatMap(err => Object.values(err).filter(Boolean))
                )
            ].length > 0 && (
                <div ref={errorSummaryRef}>
                    <StatusMessage kind="error" onClose={onCloseErrorSummary} className='mt-2'>
                        <FormErrorSummary
                            errors={[
                                ...new Set(
                                    errors.flatMap(err => Object.values(err).filter(Boolean))
                                )
                            ] as string[]}
                        />
                    </StatusMessage>
                </div>
            )}     

            {(value || []).map((rule, idx) => {
                const selectedPaths = (rule.filteringPaths || '').split(',').filter(Boolean);
                const keyForRule = `rule-${idx}`;
                const pathSearch = pathSearchMap[idx] || '';
                const selectedVirtualTenantIds = parseCsvIds(rule.appliesToVirtualTenantIds);
                const selectedVirtualTenantId = selectedVirtualTenantIds[0] || '';
                const selectedVirtualTenant = selectedVirtualTenantId
                    ? (availableVirtualTenantsMap[idx] || []).find(vt => vt.id === selectedVirtualTenantId)
                    : undefined;
                const associatedPathRows: AlarmPathRow[] | undefined = associatedAlarmPaths !== undefined
                    ? associatedAlarmPaths
                        .map(item => {
                            const pathId = Number(item.alarmPathId ?? item.id);
                            if (Number.isNaN(pathId) || pathId <= 0) {
                                return null;
                            }

                            return {
                                id: pathId,
                                pathToAlarm: item.pathToAlarm || String(pathId),
                            } as AlarmPathRow;
                        })
                        .filter((item): item is AlarmPathRow => item !== null)
                    : undefined;
                const rulePaths: AlarmPathRow[] = associatedPathRows ?? (pathsMap[idx] || []);
                const filteredPaths = rulePaths.filter(p => (p.pathToAlarm || '').toLowerCase().includes(pathSearch.toLowerCase()));

                const togglePathForRule = (ruleIndex: number, id: string) => {
                    const cur = (value[ruleIndex].filteringPaths || '').split(',').filter(Boolean);
                    const next = cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id];
                    updateRule(ruleIndex, 'filteringPaths', next.join(','));
                };

                const removePathFromRule = (ruleIndex: number, id: string) => {
                    const cur = (value[ruleIndex].filteringPaths || '').split(',').filter(Boolean);
                    const next = cur.filter(s => s !== id);
                    updateRule(ruleIndex, 'filteringPaths', next.join(','));
                };

                const isEditing = Boolean(editActions && idx === (editIndex ?? 0));

                return (
                    <div key={keyForRule} className="rule-entry">
                        <div className="rule-entry__header">
                            <h3 className={isEditing ? 'rule-entry__header--edit' : 'rule-entry__header--add'}>{isEditing ? `Edit Rule ${idx + 1}` : `Add Rule ${idx + 1}`}</h3>
                            <div>
                                <button type="button" className="btn__close se-icon--close" onClick={() => { if (isEditing && onCancelEditRule) { onCancelEditRule(); } else { removeRule(idx); } }} aria-label={`Remove rule ${idx + 1}`}></button>
                            </div>
                        </div>

                        

                        <div className="form__group">
                            <span className="form__label">Rule Name</span>
                            <input type="text" name={`ruleName-${idx}`} value={rule.ruleName || ''} onChange={e => updateRule(idx, 'ruleName', e.target.value)} className="form__control" disabled={disabled} autoComplete='off'/>
                        </div>

                        {!hideBuildingSelector && !siteId && (
                            <div className="form__group">
                                <span className="form__label">Select Customer</span>
                                <select 
                                    className="form__control" 
                                    value={selectedCustomerId || ''} 
                                    onChange={e => {
                                        const custId = Number(e.target.value);
                                        setSelectedCustomerId(custId || null);
                                                const next = (value || []).map((r, ridx) => {
                                                    if (ridx !== idx) {
                                                        return r;
                                                    }
                                                    return {
                                                        ...r,
                                                        appliesToSiteIds: '',
                                                        appliesToVirtualTenantIds: '',
                                                    };
                                                });
                                                onChange(next);
                                                setPathsMap(prev => ({ ...prev, [idx]: [] }));
                                                setAvailableVirtualTenantsMap(prev => ({ ...prev, [idx]: [] }));
                                    }}
                                    disabled={disabled}
                                >
                                    <option value="">-- Select a customer --</option>
                                    {customers.map(c => (
                                        <option key={c.id} value={c.id}>{c.customerName}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {!hideBuildingSelector && (
                            <div className="form__group form--csh">
                                <span className="form__label">Applies to Building / Buildings</span>
                                {sitesLoading && availableSitesForCustomer.length === 0 ? (
                                    <div className="csh-sites-loader__container">
                                        <div className="csh-sites-loader" role="status" aria-live="polite">
                                            <span className="csh-sites-loader__spinner" aria-hidden="true" />
                                            <span className="csh-sites-loader__text">Loading Buildings..</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="select2-like" ref={el => { if (openSiteIndex === idx) { siteWrapperRef.current = el; } }}>
                                            <button
                                                    type="button"
                                                    className="select2-input"
                                                    aria-controls={`site-list-${idx}`}
                                                    aria-haspopup="listbox"
                                                    aria-expanded={openSiteIndex === idx}
                                                    onClick={e => { e.stopPropagation(); setOpenSiteIndex(openSiteIndex === idx ? null : idx); setOpenAttIndex(null); setOpenDistIndex(null); if (openSiteIndex !== idx) { setTimeout(() => { const searchInput = siteWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } }}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenSiteIndex(openSiteIndex === idx ? null : idx); setOpenAttIndex(null); setOpenDistIndex(null); if (openSiteIndex !== idx) { setTimeout(() => { const searchInput = siteWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } } }}
                                                >
                                                {(rule.appliesToSiteIds || '').split(',').filter(Boolean).map(id => {
                                                    const s = availableSitesForCustomer.find(x => x.id === id);
                                                    const label = s ? s.title : id;
                                                    return (
                                                        <span key={`s-${idx}-${id}`} className="chip">
                                                            <span className="chip__label">{label}</span>
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                className="chip__remove"
                                                                aria-label="remove"
                                                                onClick={async ev => {
                                                                    ev.stopPropagation();
                                                                    const cur = (rule.appliesToSiteIds || '').split(',').filter(Boolean);
                                                                    const next = cur.filter(sid => sid !== id);
                                                                    await handleSiteSelectionChange(idx, next);
                                                                }}
                                                                onKeyDown={async e => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        const cur = (rule.appliesToSiteIds || '').split(',').filter(Boolean);
                                                                        const next = cur.filter(sid => sid !== id);
                                                                        await handleSiteSelectionChange(idx, next);
                                                                    }
                                                                }}
                                                            />
                                                        </span>
                                                    );
                                                })}
                                                <span className="select2-input__placeholder">
                                                    {(rule.appliesToSiteIds || '').trim() === '' ? 'Select building(s)…' : 'Click to add more…'}
                                                </span>
                                            </button>
                                            {openSiteIndex === idx && (
                                                <div className="select2-dropdown">
                                                    <input
                                                        type="text"
                                                        className="select2-dropdown__search"
                                                        value={siteSearchMap[idx] || ''}
                                                        onChange={e => setSiteSearchMap(prev => ({ ...prev, [idx]: e.target.value }))}
                                                        placeholder="Search buildings…"
                                                        autoFocus
                                                    />
                                                    <div className="select2-dropdown__list">
                                                        <ul id={`site-list-${idx}`}>
                                                            {availableSitesForCustomer.filter(s => (s.title || '').toLowerCase().includes((siteSearchMap[idx] || '').toLowerCase())).map(s => {
                                                                const id = s.id;
                                                                const selected = (rule.appliesToSiteIds || '').split(',').filter(Boolean).includes(id);
                                                                return (
                                                                    <GenericListItem
                                                                        key={id}
                                                                        id={id}
                                                                        label={s.title}
                                                                        ruleIdx={idx}
                                                                        selected={selected}
                                                                        onSelect={async (ri, sid) => {
                                                                            const cur = (value[ri]?.appliesToSiteIds || '').split(',').filter(Boolean);
                                                                            const next = cur.includes(sid) ? cur.filter(a => a !== sid) : [...cur, sid];
                                                                            await handleSiteSelectionChange(ri, next);
                                                                        }}
                                                                        onClearSearch={() => setSiteSearchMap(prev => ({ ...prev, [idx]: '' }))}
                                                                        onClose={() => setOpenSiteIndex(null)}
                                                                    />
                                                                );
                                                            })}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <small className="text-muted">Available: {availableSitesForCustomer.map(s => s.title).slice(0,3).join(', ')}{availableSitesForCustomer.length>3?'...':''}</small>
                                    </>
                                )}
                            </div>
                        )}

                        {!hideVirtualTenantSelector && (() => {
                            const selectedSitesForRule = parseCsvIds(rule.appliesToSiteIds);
                            const availableVirtualTenants = availableVirtualTenantsMap[idx] || [];
                            const selectedVirtualTenantIds = parseCsvIds(rule.appliesToVirtualTenantIds);
                            const filteredVirtualTenants = availableVirtualTenants
                                .filter(vt => vt.title.toLowerCase().includes((vtSearchMap[idx] || '').toLowerCase()));
                            const selectedVirtualTenant = selectedVirtualTenantIds.length > 0
                                ? availableVirtualTenants.find(x => x.id === selectedVirtualTenantIds[0])
                                : undefined;

                            if (selectedSitesForRule.length === 0 || availableVirtualTenants.length === 0) {
                                return null;
                            }

                            return (
                                <div className="form__group">
                                    <span className="form__label">Apply to Virtual Tenant <small>(one virtual tenant per rule)</small></span>
                                    <div className="select2-like" ref={el => { if (openVtIndex === idx) { vtWrapperRef.current = el; } }}>
                                        <button
                                            type="button"
                                            className="select2-input"
                                            aria-controls={`vt-list-${idx}`}
                                            aria-haspopup="listbox"
                                            aria-expanded={openVtIndex === idx}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setOpenVtIndex(openVtIndex === idx ? null : idx);
                                                if (openVtIndex !== idx) {
                                                    setTimeout(() => {
                                                        const searchInput = vtWrapperRef.current?.querySelector(SEARCH_SELECTOR);
                                                        (searchInput as HTMLInputElement | null)?.focus();
                                                    }, 0);
                                                }
                                            }}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setOpenVtIndex(openVtIndex === idx ? null : idx);
                                                    if (openVtIndex !== idx) {
                                                        setTimeout(() => {
                                                            const searchInput = vtWrapperRef.current?.querySelector(SEARCH_SELECTOR);
                                                            (searchInput as HTMLInputElement | null)?.focus();
                                                        }, 0);
                                                    }
                                                }
                                            }}
                                        >
                                            {selectedVirtualTenantIds.length > 0 && (() => {
                                                const vtId = selectedVirtualTenantIds[0];
                                                const vtLabel = selectedVirtualTenant ? selectedVirtualTenant.title : `VT ${vtId}`;
                                                return (
                                                    <span key={`vt-${idx}-${vtId}`} className="chip">
                                                        <span className="chip__label">{vtLabel}</span>
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            className="chip__remove"
                                                            aria-label="remove"
                                                            onClick={async ev => {
                                                                ev.stopPropagation();
                                                                await handleVirtualTenantSelectionChange(idx, null);
                                                            }}
                                                            onKeyDown={async e => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    await handleVirtualTenantSelectionChange(idx, null);
                                                                }
                                                            }}
                                                        />
                                                    </span>
                                                );
                                            })()}
                                            <span className="select2-input__placeholder">
                                                    {selectedVirtualTenantIds.length === 0 ? 'Select a virtual tenant…' : 'Click to change…'}
                                            </span>
                                        </button>
                                        {openVtIndex === idx && (
                                            <div className="select2-dropdown">
                                                <input
                                                    type="text"
                                                    className="select2-dropdown__search"
                                                    value={vtSearchMap[idx] || ''}
                                                    onChange={e => setVtSearchMap(prev => ({ ...prev, [idx]: e.target.value }))}
                                                    placeholder="Search virtual tenants…"
                                                    autoFocus
                                                />
                                                <div className="select2-dropdown__list">
                                                    <ul id={`vt-list-${idx}`}>
                                                        {filteredVirtualTenants.map(vt => {
                                                            const selected = selectedVirtualTenantIds.includes(vt.id);
                                                            return (
                                                                <GenericListItem
                                                                    key={vt.id}
                                                                    id={vt.id}
                                                                    label={vt.title}
                                                                    ruleIdx={idx}
                                                                    selected={selected}
                                                                    onSelect={async (ri, selectedVtId) => {
                                                                        await handleVirtualTenantSelectionChange(ri, selectedVtId);
                                                                    }}
                                                                    onClearSearch={clearVtSearch}
                                                                    onClose={closeVt}
                                                                />
                                                            );
                                                        })}
                                                        {filteredVirtualTenants.length === 0 && (
                                                            <li className="select2-list-item"><div className="text-muted">No virtual tenants found for selected site(s).</div></li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <small className="text-muted">Optional: the rule is scoped to this virtual tenant. Selected parent site paths are associated to it; if none are selected, the tenant's existing paths are used.</small>
                                </div>
                            );
                        })()}

                        <div className="form__row">
                            <div className="form__group">
                                <label className="form__label"><input type="checkbox" name={`isActive-${idx}`} checked={!!rule.isActive} onChange={e => updateRule(idx, 'isActive', (e.target as HTMLInputElement).checked)} disabled={disabled} /> Active</label>
                            </div>
                            <div className="form__group">
                                <label className="form__label"><input type="checkbox" name={`isUltraCritical-${idx}`} checked={!!rule.isUltraCritical} onChange={e => updateRule(idx, 'isUltraCritical', (e.target as HTMLInputElement).checked)} disabled={disabled} /> Ultra Critical Alarm</label>
                            </div>
                        </div>

                        <div className="form__group">
                            <span className="form__label">Distribution Groups</span>
                            <div
                                className="select2-like"
                                ref={el => { if (openDistIndex === idx) { distWrapperRef.current = el; } }}
                            >
                                <button
                                    type="button"
                                    className="select2-input"
                                    aria-controls={`dist-list-${idx}`}
                                    aria-haspopup="listbox"
                                    aria-expanded={openDistIndex === idx}
                                    onClick={e => { e.stopPropagation(); setOpenDistIndex(openDistIndex === idx ? null : idx); setOpenAttIndex(null); if (openDistIndex !== idx) { setTimeout(() => { const searchInput = distWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } }}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenDistIndex(openDistIndex === idx ? null : idx); setOpenAttIndex(null); if (openDistIndex !== idx) { setTimeout(() => { const searchInput = distWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } } }}
                                >
                                    {(rule.distributionGroupIds || '').split(',').filter(Boolean).map(id => {
                                        const g = groups.find(x => String(x.id) === id);
                                        const label = g ? g.profileName : id;
                                            return (
                                                <span key={`d-${idx}-${id}`} className="chip">
                                                    <span className="chip__label">{label}</span>
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        className="chip__remove"
                                                        aria-label="remove"
                                                        onClick={ev => { ev.stopPropagation(); removeDistFromRule(idx, id); }}
                                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); removeDistFromRule(idx, id); } }}
                                                    />
                                                </span>
                                            );
                                    })}
                                    <span className="select2-input__placeholder">
                                        {(rule.distributionGroupIds || '').trim() === '' ? 'Select distribution groups…' : 'Click to add more…'}
                                    </span>
                                </button>
                                {openDistIndex === idx && (
                                    <div className="select2-dropdown">
                                        <input
                                            type="text"
                                            className="select2-dropdown__search"
                                            value={distSearchMap[idx] || ''}
                                            onChange={e => setDistSearchMap(prev => ({ ...prev, [idx]: e.target.value }))}
                                            placeholder="Search distribution groups…"
                                            autoFocus
                                        />
                                        <div className="select2-dropdown__list">
                                            <ul id={`dist-list-${idx}`}>
                                                {groups.filter(g => g.profileName.toLowerCase().includes((distSearchMap[idx] || '').toLowerCase())).map(g => {
                                                    const id = String(g.id);
                                                    const selected = (rule.distributionGroupIds || '').split(',').filter(Boolean).includes(id);
                                                    return (
                                                        <GenericListItem
                                                            key={id}
                                                            id={id}
                                                            label={g.profileName}
                                                            ruleIdx={idx}
                                                            selected={selected}
                                                            onSelect={toggleDistForRule}
                                                            onClearSearch={clearDistSearch}
                                                            onClose={closeDist}
                                                        />
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <small className="text-muted">Available: {groups.map(g => g.profileName).slice(0,3).join(', ')}{groups.length>3?'...':''}</small>
                        </div>

                        <div className="form__group">
                            <span className="form__label">Attachment Groups</span>
                            <div
                                className="select2-like"
                                ref={el => { if (openAttIndex === idx) { attWrapperRef.current = el; } }}
                            >
                                <button
                                type="button"
                                className="select2-input"
                                aria-controls={`att-list-${idx}`}
                                aria-haspopup="listbox"
                                aria-expanded={openAttIndex === idx}
                                onClick={e => { e.stopPropagation(); setOpenAttIndex(openAttIndex === idx ? null : idx); setOpenDistIndex(null); if (openAttIndex !== idx) { setTimeout(() => { const searchInput = attWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } }}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenAttIndex(openAttIndex === idx ? null : idx); setOpenDistIndex(null); if (openAttIndex !== idx) { setTimeout(() => { const searchInput = attWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } } }}
                                >
                                    {(rule.attachmentGroupIds || '').split(',').filter(Boolean).map(id => {
                                        const a = attachmentGroups.find(x => String(x.id) === id);
                                        const label = a ? a.profileName : id;
                                        return (
                                            <span key={`a-${idx}-${id}`} className="chip">
                                                <span className="chip__label">{label}</span>
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    className="chip__remove"
                                                    aria-label="remove"
                                                    onClick={ev => { ev.stopPropagation(); removeAttFromRule(idx, id); }}
                                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); removeAttFromRule(idx, id); } }}
                                                />
                                            </span>
                                        );
                                    })}
                                    <span className="select2-input__placeholder">
                                        {(rule.attachmentGroupIds || '').trim() === '' ? 'Select attachment groups…' : 'Click to add more…'}
                                    </span>
                                </button>
                                    {openAttIndex === idx && (
                                        <div className="select2-dropdown">
                                            <input
                                                type="text"
                                                className="select2-dropdown__search"
                                                value={attSearchMap[idx] || ''}
                                                onChange={e => setAttSearchMap(prev => ({ ...prev, [idx]: e.target.value }))}
                                                placeholder="Search attachment groups…"
                                                autoFocus
                                            />
                                            <div className="select2-dropdown__list">
                                                <ul id={`att-list-${idx}`}>
                                                {attachmentGroups.filter(a => a.profileName.toLowerCase().includes((attSearchMap[idx] || '').toLowerCase())).map(a => {
                                                    const id = String(a.id);
                                                    const selected = (rule.attachmentGroupIds || '').split(',').filter(Boolean).includes(id);
                                                    return (
                                                        <GenericListItem
                                                            key={id}
                                                            id={id}
                                                            label={a.profileName}
                                                            ruleIdx={idx}
                                                            selected={selected}
                                                            onSelect={toggleAttForRule}
                                                            onClearSearch={clearAttSearch}
                                                            onClose={closeAtt}
                                                        />
                                                    );
                                                })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                            </div>
                            <small className="text-muted">Available: {attachmentGroups.map(a => a.profileName).slice(0,3).join(', ')}{attachmentGroups.length>3?'...':''}</small>
                        </div>

                        <div className="form__group">
                            <span className="form__label">{associatedAlarmPaths !== undefined ? 'Associated Paths' : 'Filtering - Path IDs'}</span>
                            <div style={{ marginBottom: 8 }}>
                                <small className="text-muted">
                                    {associatedAlarmPaths !== undefined
                                        ? 'Select one or more paths associated with this virtual tenant.'
                                        : selectedVirtualTenant
                                        ? 'Select one or more parent site paths to associate with the selected virtual tenant.'
                                        : `Select one or more alarm paths for this rule${parseCsvIds(rule.appliesToVirtualTenantIds).length > 0 ? ' (from selected virtual tenant parent site)' : ''}`}
                                </small>
                            </div>
                            <div className="form__group">
                                <div className="select2-like" role="group" ref={el => { if (openPathIndex === idx) {pathWrapperRef.current = el;} }}>
                                    <button
                                            type="button"
                                            className="select2-input"
                                            aria-haspopup="listbox"
                                            aria-expanded={openPathIndex === idx}
                                            aria-controls={`alarm-paths-list-${idx}`}
                                            onClick={e => { e.stopPropagation(); setOpenPathIndex(openPathIndex === idx ? null : idx); if (openPathIndex !== idx) { setTimeout(() => { const searchInput = pathWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } }}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenPathIndex(openPathIndex === idx ? null : idx); if (openPathIndex !== idx) { setTimeout(() => { const searchInput = pathWrapperRef.current?.querySelector(SEARCH_SELECTOR); (searchInput as HTMLInputElement | null)?.focus(); }, 0); } } }}
                                        >
                                        {(selectedPaths || []).map(id => {
                                            const p = rulePaths.find(x => String(x.id) === id);
                                            const fallbackLabel = rule.filteringPathLabelsById?.[id];
                                            return (
                                                <span key={id} className="chip">
                                                    <span className="chip__label">{p?.pathToAlarm || fallbackLabel || id}</span>
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        className="chip__remove"
                                                        aria-label="remove"
                                                        onClick={e => { e.stopPropagation(); removePathFromRule(idx, id); }}
                                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); removePathFromRule(idx, id); } }}
                                                    />
                                                </span>
                                            );
                                        })}
                                        <span className="select2-input__placeholder">
                                            {selectedPaths.length === 0 ? 'Select alarm paths…' : 'Click to add more…'}
                                        </span>
                                    </button>
                                    {openPathIndex === idx && (
                                        <div className="select2-dropdown select2-dropdown--paths">
                                            <input
                                                type="text"
                                                className="select2-dropdown__search"
                                                value={pathSearch}
                                                onChange={e => setPathSearchMap(prev => ({ ...prev, [idx]: e.target.value }))}
                                                placeholder="Search alarm paths…"
                                                autoFocus
                                                ref={pathSearchRef}
                                            />
                                            <div className="select2-dropdown__list">
                                                <ul id={`alarm-paths-list-${idx}`}>
                                                    {filteredPaths.length === 0 ? (
                                                        <li className="select2-list-item"><div className="text-muted">{associatedAlarmPaths !== undefined ? 'No associated paths found for this virtual tenant.' : 'No alarm paths found for this site.'}</div></li>
                                                    ) : (
                                                        filteredPaths.map(p => {
                                                            const id = String(p.id);
                                                            const selected = selectedPaths.includes(id);
                                                            return (
                                                                <GenericListItem
                                                                    key={id}
                                                                    id={id}
                                                                    label={p.pathToAlarm || id}
                                                                    ruleIdx={idx}
                                                                    selected={selected}
                                                                    onSelect={togglePathForRule}
                                                                    onClearSearch={clearPathSearchForRule}
                                                                    onClose={closePath}
                                                                />
                                                            );
                                                        })
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form__group">
                            <span className="form__label">Filtering - priorities</span>
                            <NumericChipsInput value={rule.filteringPriorities || []} onChange={(v: number[]) => updateRule(idx, 'filteringPriorities', v)} />
                            {errors[idx]?.filteringPriorities && (
                                <small className="text-danger">{errors[idx].filteringPriorities}</small>
                            )}
                            {errors[idx]?.filteringPaths && !errors[idx]?.filteringPriorities && (
                                <small className="text-danger">{errors[idx].filteringPaths}</small>
                            )}
                        </div>
                            
                        <div className="form__group">
                            <label className="form__label"><input type="checkbox" name={`bindPathPriority-${idx}`} checked={!!rule.bindPathPriority} onChange={e => updateRule(idx, 'bindPathPriority', (e.target as HTMLInputElement).checked)} disabled={disabled} /> Bind path priority</label>
                        </div>
                   

                        <div className="form__group">
                            <label className="form__label">
                                <input type="checkbox" name={`hideRtn-${idx}`} checked={!!rule.hideRtn} onChange={e => updateRule(idx, 'hideRtn', (e.target as HTMLInputElement).checked)} disabled={disabled} /> 
                                Hide RTN by Default
                                </label>
                        </div>

                        {rule.hideRtn && (
                            <div className="form__group">
                                <label className="form__label">RTN Exceptions (priorities)</label>
                                <NumericChipsInput
                                    value={(rule.rtnExceptions || '').split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n) && n > 0)}
                                    onChange={(v: number[]) => updateRule(idx, 'rtnExceptions', v.join(','))}
                                    placeholder="Add a priority and press Enter (or range like 1-20)"
                                    disabled={disabled}
                                />
                                <small className="form__hint">Alarms with these priorities will still show RTN events even when Hide RTN is enabled.</small>
                            </div>
                        )}

                        {/* Render edit actions at the foot of the rule-entry when provided and index matches */}
                        {(editActions && idx === (editIndex ?? 0)) && (
                            <div className="rule-entry__edit-actions mt-2">
                                {editActions}
                            </div>
                        )}
                    </div>
                );
            })}

            {!hideAddRuleButton && (
                <div className="form__group">
                    <button type="button" className="btn btn-add-new" onClick={addRule} disabled={disabled}>Add rule</button>
                </div>
            )}

        </div>
    );
};

export default RulesForm;
